'use client';
import { useEffect, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useAuthStore } from '@/store/auth.store';
import SidebarNav from '@/components/shell/SidebarNav';
import TopBar from '@/components/shell/TopBar';
import type { Role } from '@/lib/navigation';
import { useTheme } from '@/lib/theme';
import { useT } from '@/lib/useT';
import { useOrgBrand } from '@/lib/use-org-brand';
import { api } from '@/lib/api';
import { AlertTriangle, ShieldAlert } from 'lucide-react';
import toast from 'react-hot-toast';

// Pages that drivers CAN access
const DRIVER_ALLOWED = ['/dashboard', '/dashboard/fuel', '/dashboard/leave', '/dashboard/settings', '/dashboard/onboarding'];

// Seeker (driver without org) can access these — basically just job-search + chat + profile
const SEEKER_ALLOWED_PREFIXES = [
  '/dashboard/seeker', '/dashboard/seeker-onboarding', '/dashboard/jobs',
  '/dashboard/chat', '/dashboard/messages', '/dashboard/settings',
  '/dashboard/documents', '/dashboard/job-seeker', '/dashboard/payslips',
];

// Pages only for org admins+ (not drivers)
const ADMIN_ONLY_PREFIXES = [
  '/dashboard/drivers', '/dashboard/map', '/dashboard/fleet',
  '/dashboard/routes', '/dashboard/tachograph', '/dashboard/inventory',
  '/dashboard/reports', '/dashboard/messages', '/dashboard/ai',
  '/dashboard/marketing', '/dashboard/contacts', '/dashboard/organization',
  '/dashboard/salary', '/dashboard/analytics', '/dashboard/billing',
  '/dashboard/compliance', '/dashboard/esg', '/dashboard/maintenance',
  '/dashboard/marketplace', '/dashboard/video-storage',
  '/dashboard/tour-planner',
];

export default function DashboardLayout({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore();
  const t = useT();
  // After the localStorage→cookies migration, "is logged in" is now
  // equivalent to "we have a cached user profile". The actual JWT lives
  // in HttpOnly cookies and is unreadable from JS — so we use the user
  // object as a proxy for "session active".
  const isAuthed = !!user;
  const router = useRouter();
  const pathname = usePathname();
  const [mounted, setMounted] = useState(false);
  useTheme();
  useOrgBrand(user?.orgId);

  useEffect(() => { setMounted(true); }, []);

  // Register PWA service worker (covers /dashboard + /mini)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => {});
  }, []);
  useEffect(() => {
    if (mounted && !isAuthed) router.push('/login');
  }, [mounted, isAuthed, router]);

  // Super-admins belong on /super-admin, never on /dashboard.
  useEffect(() => {
    if (mounted && isAuthed && user?.role === 'SUPER_ADMIN') {
      router.replace('/super-admin');
    }
  }, [mounted, isAuthed, user, router]);

  // Customers belong on /customer/dashboard. The dashboard surface is
  // org-admin/driver oriented and has no rights for /api routes a customer
  // can authenticate to (most are role-gated to ADMIN/MANAGER/DRIVER).
  useEffect(() => {
    if (mounted && isAuthed && user?.role === 'CUSTOMER') {
      router.replace('/customer/dashboard');
    }
  }, [mounted, isAuthed, user, router]);

  // First-login gate: org owners without onboarding_done go to /dashboard/onboarding.
  // Skip if already there, and skip for non-owners (drivers/managers don't onboard).
  const isOwner = user?.orgRole === 'OWNER' || user?.role === 'ADMIN';
  const isSeeker = !!user && !user.orgId && user.role === 'DRIVER';
  useEffect(() => {
    if (!mounted || !isAuthed || !user || !isOwner) return;
    if (pathname.startsWith('/dashboard/onboarding')) return;
    let alive = true;
    api.get('/organizations/me/settings').then((s: any) => {
      if (!alive) return;
      if (!s?.onboarding_done) router.replace('/dashboard/onboarding');
    }).catch(() => {/* silent — don't block dashboard if settings unreachable */});
    return () => { alive = false; };
  }, [mounted, isAuthed, user, isOwner, pathname, router]);

  // Seeker gate: drivers without an org → land on /dashboard/seeker.
  // If their seeker profile isn't onboarded yet, send them through the wizard.
  useEffect(() => {
    if (!mounted || !isAuthed || !isSeeker) return;
    if (pathname.startsWith('/dashboard/seeker')) return;
    if (pathname === '/dashboard') {
      router.replace('/dashboard/seeker');
      return;
    }
    // If they navigated to a non-seeker page, just let layout block it below.
  }, [mounted, isAuthed, isSeeker, pathname, router]);

  // 2FA enforcement for privileged roles. Hooks MUST be declared before any
  // early return so React's call order stays stable across renders.
  const isPrivileged = user?.role === 'ADMIN' || user?.orgRole === 'OWNER' || user?.orgRole === 'ADMIN';
  const [twoFaStatus, setTwoFaStatus] = useState<'unknown' | 'enabled' | 'disabled'>('unknown');
  const [twoFaBannerDismissed, setTwoFaBannerDismissed] = useState(false);
  useEffect(() => {
    if (!mounted || !isPrivileged) return;
    api.get('/auth/2fa/status').then((s: any) => {
      setTwoFaStatus(s?.enabled ? 'enabled' : 'disabled');
    }).catch(() => {/* silent */});
  }, [mounted, isPrivileged]);

  if (!mounted) return null;
  if (!isAuthed) return null;
  if (user?.role === 'SUPER_ADMIN') return null;

  const isDriver = user?.role === 'DRIVER' || user?.orgRole === 'DRIVER';

  // Block drivers from admin pages
  const isBlockedPage = isDriver && ADMIN_ONLY_PREFIXES.some(p => pathname.startsWith(p));

  // Seekers (drivers w/o org) get a strict allow-list — no time-tracking, fleet, etc.
  const seekerBlocked = isSeeker && !SEEKER_ALLOWED_PREFIXES.some(p => pathname === p || pathname.startsWith(p + '/'));

  const showEmailBanner = user && user.emailVerified === false;
  const showTwoFaBanner = isPrivileged && twoFaStatus === 'disabled' && !twoFaBannerDismissed
    && !pathname.startsWith('/dashboard/settings/security');

  const resendVerification = async () => {
    try {
      await api.post('/auth/resend-verification');
      toast.success('Bestaetigungs-E-Mail erneut gesendet');
    } catch {
      toast.error('Fehler beim Senden');
    }
  };

  // Роль определяет модель навигации: диспетчер — двухуровневая,
  // водитель и соискатель — плоская (узкий workflow, группировать нечего).
  const navRole: Role = isSeeker ? 'applicant' : isDriver ? 'driver' : 'admin';

  return (
    <div className="min-h-screen bg-[var(--background)]">
      <SidebarNav role={navRole} />
      <div
        className="min-h-screen flex flex-col transition-[margin-left] duration-150 ease-out"
        style={{ marginLeft: 'var(--sidebar-w, 232px)' }}
      >
        <TopBar role={navRole} />
        <main className="flex-1 p-5">
        {showEmailBanner && (
          <div className="mb-4 flex items-center gap-2.5 px-3.5 py-2.5 rounded-[var(--radius-md)] text-[13px]
                          bg-[var(--warning-subtle)] border border-[color-mix(in_srgb,var(--warning)_30%,transparent)] text-[var(--warning)]">
            <AlertTriangle size={15} />
            <span>Bitte bestaetigen Sie Ihre E-Mail-Adresse.</span>
            <button onClick={resendVerification} className="ml-auto text-[12px] font-semibold underline hover:no-underline">
              Erneut senden
            </button>
          </div>
        )}

        {/* Включить 2FA — рекомендация, а не отказ системы: красная полоса во всю
            ширину читалась как критическая авария. Тон warning, компактно, справа. */}
        {showTwoFaBanner && (
          <div className="mb-4 flex justify-end">
            <div className="inline-flex items-center gap-2.5 pl-3 pr-2 py-2 max-w-full rounded-[var(--radius-md)]
                            bg-[var(--warning-subtle)] border border-[color-mix(in_srgb,var(--warning)_28%,transparent)]">
              <ShieldAlert size={14} className="text-[var(--warning)] shrink-0" />
              <span className="text-[12.5px] text-[var(--text-secondary)] truncate">
                {t('twofa_banner_title')}
              </span>
              <a href="/dashboard/settings/security"
                 className="text-[12.5px] font-medium text-[var(--warning)] hover:underline whitespace-nowrap">
                {t('twofa_banner_cta')}
              </a>
              <button onClick={() => setTwoFaBannerDismissed(true)}
                      className="w-6 h-6 grid place-items-center rounded-[var(--radius-xs)] text-[var(--text-muted)]
                                 hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)] transition-colors"
                      title={t('twofa_banner_dismiss')} aria-label={t('twofa_banner_dismiss')}>✕</button>
            </div>
          </div>
        )}

        {isBlockedPage || seekerBlocked ? (
          <div className="flex flex-col items-center justify-center h-64 text-center gap-2">
            <ShieldAlert size={28} className="text-[var(--text-muted)]" />
            <h2 className="text-[15px] font-semibold text-[var(--text-primary)]">Kein Zugriff</h2>
            <p className="text-[13px] max-w-[52ch] text-[var(--text-muted)]">
              {seekerBlocked
                ? 'Sie sind als unabhaengiger Fahrer registriert. Funktionen wie Zeiterfassung, Flotte usw. werden freigeschaltet, sobald Sie von einer Spedition aufgenommen werden.'
                : 'Diese Seite ist nur fuer Administratoren verfuegbar.'}
            </p>
            <a href={seekerBlocked ? '/dashboard/seeker' : '/dashboard'}
               className="mt-2 inline-flex items-center h-9 px-3.5 rounded-[var(--radius-sm)] text-[13px] font-medium
                          bg-[var(--brand)] text-[var(--on-brand)] hover:bg-[var(--brand-hover)] transition-colors">
              {seekerBlocked ? 'Zu meinem Marktplatz' : 'Zum Dashboard'}
            </a>
          </div>
        ) : (
          children
        )}
        </main>
      </div>
    </div>
  );
}
